import React from 'react';
import type { Variants } from 'framer-motion';

/* One motion vocabulary for the whole site. Every reveal, draw and settle on
   the page is one of these curves at one of these durations, so the page reads
   as authored rather than as a pile of separate effects.

   Everything here animates transform, opacity or clip-path only. Nothing
   animates layout. */

export type Bezier = [number, number, number, number];

/* The house curve: fast out of the gate, long settle, no overshoot. */
export const EASE_OUT: Bezier = [0.16, 1, 0.3, 1];
/* Apple's standard UI curve, for state changes rather than entrances. */
export const EASE_STD: Bezier = [0.32, 0.72, 0, 1];

export const DUR = { fast: 0.14, base: 0.24, slow: 0.42, glass: 0.64 } as const;
export const STAGGER = { tight: 0.05, base: 0.08, loose: 0.12 } as const;

const enter = { duration: DUR.glass, ease: EASE_OUT };

export const fadeUp: Variants = {
    hidden: { opacity: 0, y: 18 },
    shown: { opacity: 1, y: 0, transition: enter },
};

export const fadeIn: Variants = {
    hidden: { opacity: 0 },
    shown: { opacity: 1, transition: enter },
};

/* A slab arriving: a little further, a little smaller, same curve. */
export const rise: Variants = {
    hidden: { opacity: 0, y: 28, scale: 0.98 },
    shown: { opacity: 1, y: 0, scale: 1, transition: enter },
};

/* Write-on. The resting inset is negative on every side, including the left the
   wipe starts from, so the finished element is never clipped: rough-notation
   ellipses and ink arrows hang well outside their box. Motion.tsx also drops the
   clip entirely once the move lands, so nothing survives to crop a late mark. */
export const clipLeft: Variants = {
    hidden: { opacity: 0, clipPath: 'inset(-40% 100% -40% -14%)' },
    shown: {
        opacity: 1,
        clipPath: 'inset(-40% -40% -40% -14%)',
        transition: { duration: DUR.slow, ease: EASE_OUT },
    },
};

/* A node landing on the ring. */
export const pop: Variants = {
    hidden: { opacity: 0, scale: 0.2 },
    shown: { opacity: 1, scale: 1, transition: { duration: DUR.base, ease: EASE_OUT } },
};

/* A polaroid landing on the table: falls in, straightens the last few degrees.
   The rest rotation lives in CSS, so this rotates relative to it. */
export const drop: Variants = {
    hidden: { opacity: 0, y: -24, rotate: -4 },
    shown: { opacity: 1, y: 0, rotate: 0, transition: { duration: 0.52, ease: EASE_OUT } },
};

/* No move at all. For a Reveal used purely as an in-view sensor, where the
   children own their own entrance. */
export const hold: Variants = { hidden: { opacity: 1 }, shown: { opacity: 1 } };

export const VARIANTS = { fadeUp, fadeIn, rise, clipLeft, pop, drop, hold } as const;
export type VariantName = keyof typeof VARIANTS;

export function container(stagger: number = STAGGER.base, delayChildren = 0): Variants {
    return {
        hidden: {},
        shown: { transition: { staggerChildren: stagger, delayChildren } },
    };
}

function reducedNow() {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* True when the visitor has not asked for reduced motion. Read once at mount
   and kept in sync, so a mid-session preference change is honoured. */
export function useMotionOK() {
    const [ok, setOk] = React.useState(() => !reducedNow());
    React.useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const onChange = () => setOk(!mq.matches);
        onChange();
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return ok;
}

function easeOutAt(t: number) {
    /* Closed-form sample of EASE_OUT, accurate enough for a counter. */
    return 1 - Math.pow(1 - t, 3);
}

/* Counts an integer up once, on demand. Reduced motion lands on the target
   immediately, so the number is never wrong or missing. */
export function useCountUp(target: number, { from = 0, duration = 900, run = true } = {}) {
    const motionOK = useMotionOK();
    const [value, setValue] = React.useState(motionOK ? from : target);

    React.useEffect(() => {
        if (!run) return;
        if (!motionOK) {
            setValue(target);
            return;
        }
        let raf = 0;
        let start = 0;
        const tick = (now: number) => {
            if (!start) start = now;
            const t = Math.min(1, (now - start) / duration);
            setValue(Math.round(from + (target - from) * easeOutAt(t)));
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, from, duration, run, motionOK]);

    return value;
}

/* Same move, later. Framer replaces a variant's transition wholesale when a
   `transition` prop is set on the component, so the delay is merged in here
   instead of passed alongside. */
export function withDelay(v: Variants, delay: number): Variants {
    if (!delay) return v;
    const shown = v.shown as Record<string, unknown>;
    const transition = (shown?.transition as Record<string, unknown>) ?? enter;
    return { ...v, shown: { ...shown, transition: { ...transition, delay } } };
}
